import { type Permission } from '@/lib/permissions';
import { useAuth } from './useAuth';

/**
 * Hook for checking user permissions and roles
 *
 * Note: These functions return false when user is null (not authenticated).
 * ProtectedRoute should check isAuthenticated before checking permissions.
 */
export function usePermissions() {
  const { user, isAuthenticated } = useAuth();

  /**
   * Check if user has a specific permission.
   * The `permission` parameter must be a value from the PERMISSIONS constant
   * (i.e. a `Permission` type) so that TypeScript catches typos at build time.
   * Returns false if user is not authenticated.
   */
  const hasPermission = (permission: Permission): boolean => {
    if (!isAuthenticated || !user?.permissions) return false;
    return user.permissions.includes(permission);
  };

  /**
   * Check if user has any of the provided permissions.
   * Returns false if user is not authenticated.
   */
  const hasAnyPermission = (permissions: Permission[]): boolean => {
    if (!isAuthenticated || !user?.permissions || permissions.length === 0) return false;
    return permissions.some((permission) => user.permissions.includes(permission));
  };

  /**
   * Check if user has all of the provided permissions.
   * Returns false if user is not authenticated.
   */
  const hasAllPermissions = (permissions: Permission[]): boolean => {
    if (!isAuthenticated || !user?.permissions || permissions.length === 0) return false;
    return permissions.every((permission) => user.permissions.includes(permission));
  };

  /**
   * Check if user has a specific role
   * Returns false if user is not authenticated
   */
  const hasRole = (role: string): boolean => {
    if (!isAuthenticated || !user?.roles) return false;
    return user.roles.includes(role);
  };

  /**
   * Check if user has any of the provided roles
   * Returns false if user is not authenticated
   */
  const hasAnyRole = (roles: string[]): boolean => {
    if (!isAuthenticated || !user?.roles || roles.length === 0) return false;
    return roles.some((role) => user.roles.includes(role));
  };

  /**
   * Check if user has all of the provided roles
   * Returns false if user is not authenticated
   */
  const hasAllRoles = (roles: string[]): boolean => {
    if (!isAuthenticated || !user?.roles || roles.length === 0) return false;
    return roles.every((role) => user.roles.includes(role));
  };

  return {
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    hasRole,
    hasAnyRole,
    hasAllRoles,
  };
}
